import { useEffect, useState, type FC, type ReactNode } from "react";
import { useStickToBottomContext } from "use-stick-to-bottom";

import { cn } from "@/lib/utils";

type Props = {
  /**
   * Make the turn at least one viewport tall. With the transcript stuck to
   * the bottom, that puts the turn's user message at the top of the pane and
   * leaves the space below it empty, which leads the eye to the question
   * docked on the composer.
   */
  anchor: boolean;
  /** Vertical padding of the scrolling content, taken off the viewport height. */
  inset?: number;
  className?: string;
  children: ReactNode;
};

/**
 * Wrapper for the latest turn (the last user message and everything after
 * it). Must render inside `Conversation` so it can read the scroll viewport.
 * When `anchor` is off it is a plain flex column, so nothing remounts as the
 * agent's question appears and is answered.
 */
export const LatestTurn: FC<Props> = ({
  anchor,
  inset = 24,
  className,
  children
}) => {
  const { scrollRef } = useStickToBottomContext();
  const [viewport, setViewport] = useState<number | null>(null);

  useEffect(() => {
    if (!anchor) {
      setViewport(null);
      return undefined;
    }
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewport(el.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [anchor, scrollRef]);

  const minHeight =
    anchor && viewport ? Math.max(0, viewport - inset) : undefined;

  return (
    <div
      className={cn("flex flex-col gap-4", className)}
      data-testid="latest-turn"
      data-anchored={anchor ? "" : undefined}
      style={minHeight ? { minHeight } : undefined}
    >
      {children}
    </div>
  );
};
